import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  viewName?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ViewErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ViewErrorBoundary caught an error]:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  private handleRetry = () => {
    if (typeof window !== 'undefined' && 'caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      }).catch(() => {});
    }
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (const reg of registrations) reg.unregister();
      }).catch(() => {});
    }

    if (this.props.onReset) {
      try {
        this.props.onReset();
      } catch (e) {
        // ignore
      }
    }

    if (typeof window !== 'undefined') {
      window.location.reload();
    } else {
      this.setState({ hasError: false, error: null, errorInfo: null });
    }
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="h-[calc(100vh-10rem)] w-full flex flex-col items-center justify-center p-8 bg-white dark:bg-surface-900 rounded-3xl border border-surface-200 dark:border-surface-800 shadow-card">
          <div className="w-14 h-14 rounded-2xl bg-amber-500/10 text-amber-600 dark:text-amber-400 flex items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 stroke-[2]" />
          </div>

          <h3 className="text-lg font-bold text-surface-900 dark:text-surface-100 mb-1">
            Unable to display {this.props.viewName || 'this view'}
          </h3>

          <p className="text-xs text-surface-500 max-w-md text-center mb-4">
            An unexpected error occurred while rendering. All your data remains safe and synchronized in the background.
          </p>

          {this.state.error && (
            <div className="max-w-md w-full p-3 mb-6 bg-surface-50 dark:bg-surface-950/60 rounded-xl border border-surface-200 dark:border-surface-800 text-[11px] font-mono text-rose-600 dark:text-rose-400 overflow-x-auto">
              {this.state.error.message || String(this.state.error)}
            </div>
          )}

          <button
            onClick={this.handleRetry}
            className="px-4 py-2 bg-[#121417] hover:bg-black dark:bg-white dark:hover:bg-surface-100 text-white dark:text-black rounded-xl font-bold text-xs shadow-sm flex items-center space-x-2 transition-all hover:scale-105 active:scale-95"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>Reload View</span>
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
